import { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
// import { useOnlyForDev } from '@/utils';
import { useQueryClient } from '@common-fe/common-fe';
import { HealthPlanStatuses, removeEmptyObjects } from '@common-fe/common-fe';
import _ from 'lodash';

import { useCurrentOrganisationStore } from '@/modules/core/store';
import {
  CategorySpendLimitConfig,
  HealthPlanDeductibleConfig,
  HealthPlanResponse,
} from '@/modules/HealthPlan/HealthPlan.types';

import { HEALTH_PLAN_LIST_QUERY_NAME } from '../../queries/useHealthPlansList.query';
import {
  CoverageLevelPayload,
  CUSTOM_TRIGGER_AMOUNT_PREFIX,
  DEDUCTIBLE_FIELDS,
} from '../CoverageLevels/CoverageLevels.constants';
import { useUpdateHealthPlanByIdQuery } from '../queries';
import { QUERY_KEY } from '../queries/useHealthPlanById.query';
import { usePlanLoadingStore } from '../stores';

import useHealthPlanSetupFieldsState from './useHealthPlanSetupFieldsState';
import useHealthPlanSetupState from './useHealthPlanSetupState';

export const YES_KEY = 'true';
export const NO_KEY = 'false';
const DELAY_TIMEOUT = 1500;

const getBooleanFromYesNo = (value?: string): boolean | undefined => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
};

const mapFundingConfigToPayload = (fundingConfig: {
  id?: string;
  planId?: string;
  autoEnrollmentAmount?: string;
  perPersonSpendLimit?: string;
}) => {
  const perPersonSpendLimitAmount = fundingConfig.perPersonSpendLimit
    ? _.toNumber(fundingConfig.perPersonSpendLimit)
    : null;

  return _.pickBy(
    {
      id: _.toNumber(fundingConfig.id) || undefined,
      plan_id: _.toNumber(fundingConfig.planId) || undefined,
      auto_enrollment_amount:
        fundingConfig.autoEnrollmentAmount === ''
          ? undefined
          : _.toNumber(fundingConfig.autoEnrollmentAmount),
      per_person_spend_limit_amount: perPersonSpendLimitAmount,
    },
    (v) => v != null
  );
};

interface Config {
  errorCallback?: (error: unknown) => void;
  isTemplateMode?: boolean;
  newCoverageIds?: number[];
  removeCoverageId?: number;
  isReloadlessMode?: boolean;
}

export default (config: Config) => {
  const setLoading = usePlanLoadingStore((state) => state.setLoading);
  const queryClient = useQueryClient();
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const fieldsState = useHealthPlanSetupFieldsState();
  const state = useHealthPlanSetupState();
  const mutation = useUpdateHealthPlanByIdQuery(id, config.errorCallback);

  const { observingOrganizationId } = useCurrentOrganisationStore();
  const formattedPlans = useMemo(() => {
    if (state.plans && state.plans?.length > 0) {
      return state.plans.filter((item) => typeof item === 'string');
    }
    return [];
  }, [state.plans]);

  const hasLinkedPlans = formattedPlans.length > 0;

  const formatedDeductibles = useMemo(() => {
    const list = state.coverageLevels.reduce(
      (result: HealthPlanDeductibleConfig[], item: CoverageLevelPayload) => {
        if (item) {
          const currentCoverageFieldState = fieldsState.coverageLevels.find(
            (coverageField) => coverageField.coverageId === item.coverageId
          );

          // Build trigger_configs: only include triggers that have an amount
          const triggerConfigs = item.customTriggerAmounts
            ? Object.entries(item.customTriggerAmounts)
                .filter(([, amount]) => !!amount)
                .map(([key, amount]) => ({
                  trigger_id: _.toNumber(key.replace(CUSTOM_TRIGGER_AMOUNT_PREFIX, '')),
                  threshold_amount: _.toNumber(amount),
                }))
            : DEDUCTIBLE_FIELDS.reduce<{ trigger_id: number; threshold_amount: number }[]>(
                (acc, { name, triggerId }) => {
                  const numValue = _.toNumber(_.get(item, name));
                  if (numValue) {
                    acc.push({ trigger_id: triggerId, threshold_amount: numValue });
                  }
                  return acc;
                },
                []
              );

          return [
            ...result,
            {
              id: _.toNumber(item.id) || undefined,
              parent_id: _.toNumber(item.parentId) || undefined,
              // coverage_id: _.toNumber(item.coverageId) || undefined,
              coverage: {
                id: _.toNumber(item.coverageId) || undefined,
                name: item.name,
                is_default: !item.isDeletable,
              },
              code: _.pickBy({
                code: item.coverageCode,
                code_state: currentCoverageFieldState?.coverageCode,
              }),
              trigger_configs: triggerConfigs,

              auto_enroll_account_funding_configs:
                !config.isTemplateMode && hasLinkedPlans
                  ? item?.accountFundingConfigs?.map(mapFundingConfigToPayload)
                  : undefined,
            },
          ];
        }
        return result;
      },
      []
    );

    const filteredList = config.removeCoverageId
      ? list.filter((item) => !item.id || !!item.parent_id || item.id !== config.removeCoverageId)
      : list;

    const parentIdFilteredList = config.removeCoverageId
      ? filteredList.map((item) => {
          if (item.id === config.removeCoverageId && item.parent_id) {
            return {
              parent_id: item.parent_id,
            };
          }
          return item;
        })
      : filteredList;
    const expandedList = config.newCoverageIds
      ? [
          ...parentIdFilteredList,
          ...config.newCoverageIds.map((coverageId) => ({
            coverage_id: coverageId,
          })),
        ]
      : parentIdFilteredList;

    return expandedList;
  }, [
    state.coverageLevels,
    config.removeCoverageId,
    config.newCoverageIds,
    config.isTemplateMode,
    fieldsState.coverageLevels,
    hasLinkedPlans,
  ]);

  const formatedData = useMemo(() => {
    let isAutoCreateCdhEnrollment: boolean | undefined;
    // It needed because it's not a checkbox, it's radiobutton value with Yes/No answers
    if (state.autocreateCdhEnrollment === YES_KEY) {
      isAutoCreateCdhEnrollment = true;
    } else if (state.autocreateCdhEnrollment === NO_KEY) {
      isAutoCreateCdhEnrollment = false;
    }

    // Collect category spend limits from all coverage levels
    const categorySpendLimits: CategorySpendLimitConfig[] = [];
    if (hasLinkedPlans && getBooleanFromYesNo(state.perCategorySpendLimit)) {
      // Fallback plan_id from linked plans when not set on the limit itself
      const fallbackPlanId = formattedPlans.length > 0 ? _.toNumber(formattedPlans[0]) : undefined;

      state.coverageLevels.forEach((coverageLevel) => {
        coverageLevel?.categorySpendLimits?.forEach((limit) => {
          if (limit.categorySpendLimit) {
            const planId = limit.planId ? _.toNumber(limit.planId) : fallbackPlanId;
            categorySpendLimits.push({
              ...(limit.id ? { id: _.toNumber(limit.id) } : {}),
              ...(planId ? { plan_id: planId } : {}),
              health_plan_id: _.toNumber(id),
              coverage_id: _.toNumber(coverageLevel.coverageId) || undefined,
              category_type: limit.categoryType,
              category_spend_limit: parseFloat(limit.categorySpendLimit),
            });
          }
        });
      });
    }

    const payload: Partial<HealthPlanResponse> = removeEmptyObjects({
      id,
      is_plan: !config.isTemplateMode,
      ...(state.planSummary
        ? {
            summary: state.planSummary,
          }
        : {}),
      plan_status: HealthPlanStatuses.DRAFT,
      plan_code: state.planCode,
      notes: state.healthPlanNotes,
      plan_year_id: state.planYearId ? _.toNumber(state.planYearId) : undefined,
      ...(observingOrganizationId
        ? {
            organization_id: observingOrganizationId,
          }
        : {}),
      name: {
        name: state.healthPlanName,
        name_state: fieldsState.healthPlanName,
      },
      carrier_id: {
        carrier_id: state.carrierName ? _.toNumber(state.carrierName) : undefined,
        carrier_id_state: fieldsState.carrierName,
      },
      health_plan_types: {
        health_plan_types: state.healthPlanTypes,
        health_plan_type_state: fieldsState.healthPlanTypes,
      },
      ...(isAutoCreateCdhEnrollment !== undefined && hasLinkedPlans
        ? {
            is_auto_enrollment: {
              is_auto_enrollment: isAutoCreateCdhEnrollment,
              is_auto_enrollment_state: fieldsState.autocreateCdhEnrollment,
            },
          }
        : {}),
      ...(hasLinkedPlans
        ? {
            per_person_spend_limit: getBooleanFromYesNo(state.perPersonSpendLimit),
          }
        : {}),
      ...(hasLinkedPlans
        ? {
            per_category_spend_limit: getBooleanFromYesNo(state.perCategorySpendLimit),
          }
        : {}),
      health_plan_deductible_configs: formatedDeductibles,
      plans: formattedPlans,
      deductibles_by_service_type: {
        deductibles_by_service_type: state.deductibleService || undefined,
        deductibles_by_service_type_state: fieldsState.deductibleService,
      },
    });
    if (hasLinkedPlans && getBooleanFromYesNo(state.perCategorySpendLimit)) {
      payload.category_spend_limits = categorySpendLimits;
    }

    return payload;
  }, [
    state,
    fieldsState,
    id,
    observingOrganizationId,
    config,
    formattedPlans,
    formatedDeductibles,
    hasLinkedPlans,
  ]);
  const handleSave = useCallback(
    async (planStatus: HealthPlanStatuses) => {
      await mutation.mutateAsync({
        dto: {
          ...formatedData,
          plan_status: planStatus,
          enrollment_matrix: {
            name: state.enrollmentMatrixName || undefined,
            processing_rule_id: state.processingRuleName ? _.toNumber(state.processingRuleName) : undefined,
          },
        },
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, id],
      });
      queryClient.invalidateQueries({
        queryKey: [HEALTH_PLAN_LIST_QUERY_NAME],
      });
      if (!config.isReloadlessMode) {
        setLoading(true);

        setTimeout(() => {
          setLoading(false);
        }, DELAY_TIMEOUT);
      }

      // handleSetSourceState(getFormatedPlan(plan));
    },
    [config.isReloadlessMode, formatedData, id, mutation, queryClient, setLoading, state]
  );

  return handleSave;
};
